import React, { useRef, useState, useEffect, useCallback } from "react";
import CkTextEditorStyles from "./styles/CkTextEditorStyles";
import LinkModal from "./Modals/LinkModal";
import ImageModal from "./Modals/ImageModal";
import TableModal from "./Modals/TableModal";
import FindReplaceModal from "./Modals/FindReplaceModal";
import EmojiPickerModal from "./Modals/EmojiPickerModal";
import VersionHistoryModal from "./Modals/VersionHistoryModal";
import SettingsModal from "./Modals/SettingsModal";
import TableContextMenu from "./Modals/TableContextMenu";
import {
  FaSmile,
  FaSave,
  FaClock,
  FaUsers,
  FaEye,
  FaCog,
  FaUndo as FaRotateCcw,
} from "react-icons/fa";
import Modal from "../Modal/Modal";
import ColorPicker from "./UI/ColorPicker";

const CkTextEditor = ({
  value = "",
  onChange = () => {},
  placeholder = "Start typing...",
  height = "200px",
  className = "",
  showWordCount = true,
  maxLength = null,
  readOnly = false,
   onFocus,
}) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [isFindReplaceOpen, setIsFindReplaceOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [selectedText, setSelectedText] = useState("");
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [wordCount, setWordCount] = useState({
    words: 0,
    characters: 0,
    charactersNoSpaces: 0,
  });
  const [currentHighlight, setCurrentHighlight] = useState("#ffff00");
  const [currentTextColor, setCurrentTextColor] = useState("#000000");
  const [currentBackgroundColor, setCurrentBackgroundColor] =
    useState("#ffffff");

  // Advanced features state
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [editorTheme, setEditorTheme] = useState("light");
  const [fontSize, setFontSize] = useState(16);
  const [lineHeight, setLineHeight] = useState(1.6);
  const [isSpellCheckEnabled, setIsSpellCheckEnabled] = useState(true);
  const [isReadingMode, setIsReadingMode] = useState(false);
  const [selectedTableCell, setSelectedTableCell] = useState(null);
  const [tableContextMenu, setTableContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    type: "",
  });
  const [documentStats, setDocumentStats] = useState({
    readingTime: 0,
    complexity: "Simple",
  });
  const [hasTextSelection, setHasTextSelection] = useState(false);
  const [savedRange, setSavedRange] = useState(null);
  const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAltText, setImageAltText] = useState("");
  const [imageWidth, setImageWidth] = useState("");
  const [imageHeight, setImageHeight] = useState("");
  const [imageAlignment, setImageAlignment] = useState("none");
  const [imageInputType, setImageInputType] = useState("url"); // "url" or "file"
  const [imageFile, setImageFile] = useState(null);
  const imageFileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isEditingExistingImage, setIsEditingExistingImage] = useState(false);
  const [selectedImageElement, setSelectedImageElement] = useState(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
      updateWordCount();
    }
  }, [value]);

  // Add selection change listeners
  useEffect(() => {
    const handleSelectionChange = () => {
      checkTextSelection();
    };

    document.addEventListener("selectionchange", handleSelectionChange);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, []);

  const calculateWordCount = useCallback((text) => {
    const plainText = text.replace(/<[^>]*>/g, "").trim();
    const words = plainText ? plainText.split(/\s+/).length : 0;
    const characters = plainText.length;
    const charactersNoSpaces = plainText.replace(/\s/g, "").length;
    return { words, characters, charactersNoSpaces };
  }, []);

  // Auto-save functionality
  useEffect(() => {
    if (isAutoSaveEnabled && value) {
      const autoSaveTimer = setTimeout(() => {
        saveVersion();
        setLastSaved(new Date());
        // Here you would typically save to your backend
        // console.log("Auto-saved at:", new Date());
      }, 3000); // Auto-save after 3 seconds of inactivity

      return () => clearTimeout(autoSaveTimer);
    }
  }, [value, isAutoSaveEnabled]);

  // Calculate document statistics
  useEffect(() => {
    const text = value.replace(/<[^>]*>/g, "");
    const words = text.trim().split(/\s+/).length;
    const avgWordsPerMinute = 200;
    const readingTime = Math.ceil(words / avgWordsPerMinute);

    // Simple complexity calculation based on sentence length and word complexity
    const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 0);
    const avgSentenceLength =
      sentences.length > 0 ? words / sentences.length : 0;
    const complexity =
      avgSentenceLength > 20
        ? "Complex"
        : avgSentenceLength > 15
        ? "Moderate"
        : "Simple";

    setDocumentStats({ readingTime, complexity });
  }, [value]);

  // Save version to history
  const saveVersion = useCallback(() => {
    if (value.trim()) {
      const newVersion = {
        id: Date.now(),
        content: value,
        timestamp: new Date(),
        wordCount: calculateWordCount(value).words,
      };
      setVersionHistory((prev) => [newVersion, ...prev.slice(0, 9)]); // Keep last 10 versions
    }
  }, [value, calculateWordCount]);

  const updateWordCount = useCallback(() => {
    if (editorRef.current && showWordCount) {
      const content = editorRef.current.innerHTML;
      setWordCount(calculateWordCount(content));
    }
  }, [calculateWordCount, showWordCount]);

  const executeCommand = (command, value = null) => {
    if (readOnly) return;

    // Use modern APIs where possible, fallback to execCommand for formatting
    try {
      if (command === "copy") {
        copyContent();
        return;
      }
      if (command === "cut") {
        cutContent();
        return;
      }

      // For formatting commands, use modern Selection API where possible
      if (command === "insertHTML" && value) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();

          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = value;
          const fragment = document.createDocumentFragment();
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }
          range.insertNode(fragment);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else {
        // For other formatting commands (bold, italic, etc.), use execCommand as last resort
        // Note: These commands are deprecated but still widely supported for contentEditable
        // Consider implementing custom formatting logic in the future
        if (typeof document.execCommand === "function") {
          document.execCommand(command, false, value);
        } else {
          console.warn(`Command '${command}' not supported in this browser`);
        }
      }
    } catch (error) {
      console.warn(`Command '${command}' failed:`, error);
    }

    editorRef.current.focus();
    handleContentChange();
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      if (maxLength && content.replace(/<[^>]*>/g, "").length > maxLength) {
        return;
      }
      onChange(content);
      updateWordCount();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];

      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file.");
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert("Image file size must be less than 5MB.");
        return;
      }

      setImageFile(file);

      // Create a preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageUrl(e.target.result);
      };
      reader.readAsDataURL(file);

      // Set alt text to filename if not already set
      if (!imageAltText) {
        setImageAltText(file.name.split(".")[0].replace(/[_-]/g, " "));
      }
    }
  };

  const handleKeyDown = (e) => {
    // Handle tab key for indentation
    if (e.key === "Tab") {
      e.preventDefault();
      executeCommand("insertHTML", "&nbsp;&nbsp;&nbsp;&nbsp;");
    }

    // Handle Ctrl+shortcuts
    if (e.ctrlKey) {
      switch (e.key) {
        case "b":
          e.preventDefault();
          executeCommand("bold");
          break;
        case "i":
          e.preventDefault();
          executeCommand("italic");
          break;
        case "u":
          e.preventDefault();
          executeCommand("underline");
          break;
        case "z":
          e.preventDefault();
          executeCommand("undo");
          break;
        case "y":
          e.preventDefault();
          executeCommand("redo");
          break;
        case "v":
          // Let the native paste event handle this
          // The onPaste handler will take care of it
          break;
        default:
          break;
      }
    }
  };

  const handlePaste = async (e) => {
    e.preventDefault();

    try {
      const clipboardData = e.clipboardData || window.clipboardData;

      if (clipboardData) {
        // Try to get HTML content first, then fall back to plain text
        let content = clipboardData.getData("text/html");

        if (!content) {
          content = clipboardData.getData("text/plain");
        }

        if (content && editorRef.current) {
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();

            if (clipboardData.getData("text/html")) {
              // Insert HTML content
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = content;

              const fragment = document.createDocumentFragment();
              while (tempDiv.firstChild) {
                fragment.appendChild(tempDiv.firstChild);
              }
              range.insertNode(fragment);
            } else {
              // Insert plain text
              range.insertNode(document.createTextNode(content));
            }

            // Move cursor to end of inserted content
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
          } else {
            // No selection, append to end
            if (clipboardData.getData("text/html")) {
              editorRef.current.insertAdjacentHTML("beforeend", content);
            } else {
              editorRef.current.appendChild(document.createTextNode(content));
            }
          }

          handleContentChange();
        }
      }
    } catch (error) {
      console.error("Failed to paste content:", error);
      // Fallback: allow default paste behavior
      // Don't prevent default in this case
    }
  };

  // Check if text is selected
  const checkTextSelection = () => {
    const selection = window.getSelection();
    const hasSelection =
      selection && selection.rangeCount > 0 && !selection.isCollapsed;
    setHasTextSelection(hasSelection);
    return hasSelection;
  };

  const insertLink = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);
      const selectedTextContent = range.toString().trim();
      if (selectedTextContent) {
        // Check if the selection contains or is within a link
        const commonAncestor = range.commonAncestorContainer;
        let linkElement = null;

        // Check if selection is within a link element
        if (commonAncestor.nodeType === Node.ELEMENT_NODE) {
          linkElement = commonAncestor.closest("a");
        } else if (commonAncestor.parentElement) {
          linkElement = commonAncestor.parentElement.closest("a");
        }

        // Check if selection contains a link element
        if (!linkElement && range.cloneContents) {
          const fragment = range.cloneContents();
          const tempDiv = document.createElement("div");
          tempDiv.appendChild(fragment);
          linkElement = tempDiv.querySelector("a");
        }

        // Save the range for later restoration
        setSavedRange(range.cloneRange());
        setSelectedText(selectedTextContent);

        // If there's an existing link, pre-populate the URL field
        if (linkElement) {
          setLinkUrl(linkElement.href || "");
          setIsEditingExistingLink(true);
        } else {
          setLinkUrl("");
          setIsEditingExistingLink(false);
        }

        setIsLinkModalOpen(true);
      } else {
        alert("Please select some text first to create a link.");
      }
    } else {
      alert("Please select some text first to create a link.");
    }
  };

  const applyLink = () => {
    if (linkUrl && selectedText && savedRange) {
      // Focus the editor first
      editorRef.current?.focus();

      try {
        // Restore the saved selection
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(savedRange);

        if (isEditingExistingLink) {
          // For existing links, we need to update the href attribute
          const commonAncestor = savedRange.commonAncestorContainer;
          let linkElement = null;

          if (commonAncestor.nodeType === Node.ELEMENT_NODE) {
            linkElement = commonAncestor.closest("a");
          } else if (commonAncestor.parentElement) {
            linkElement = commonAncestor.parentElement.closest("a");
          }

          if (linkElement) {
            linkElement.href = linkUrl;
            linkElement.target = "_blank";
          } else {
            // Fallback to createLink if we can't find the existing link
            executeCommand("createLink", linkUrl);
          }
        } else {
          // Create new link using execCommand
          executeCommand("createLink", linkUrl);
        }

        // Clear the selection
        selection.removeAllRanges();
        handleContentChange();
      } catch (error) {
        console.error("Error applying link:", error);
        // Fallback: manually create/update the link
        const linkElement = `<a href="${linkUrl}" target="_blank">${selectedText}</a>`;
        const content = editorRef.current.innerHTML;
        const newContent = content.replace(selectedText, linkElement);
        editorRef.current.innerHTML = newContent;
        handleContentChange();
      }

      setIsLinkModalOpen(false);
      setLinkUrl("");
      setSelectedText("");
      setSavedRange(null);
      setIsEditingExistingLink(false);
    }
  };

  const insertImage = () => {
    // Save the current cursor position
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      setSavedRange(range.cloneRange());
    }
    setIsImageModalOpen(true);
  };

  const handleImageFileChange = (fileOrEvent) => {
    // Handle both direct file and event object
    const file =
      fileOrEvent instanceof File
        ? fileOrEvent
        : fileOrEvent?.target?.files?.[0];

    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file.");
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert("Image file size must be less than 5MB.");
        return;
      }

      setImageFile(file);

      // Create a preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageUrl(e.target.result);
      };
      reader.readAsDataURL(file);

      // Set alt text to filename if not already set
      if (!imageAltText) {
        setImageAltText(generateAltTextFromFilename(file.name));
      }
    }
  };

  const applyImage = () => {
    const finalImageUrl =
      imageInputType === "file" && imageFile ? imageUrl : imageUrl;

    if (finalImageUrl) {
      // Focus the editor first
      editorRef.current?.focus();

      try {
        if (isEditingExistingImage && selectedImageElement) {
          // Update existing image
          selectedImageElement.src = finalImageUrl;
          selectedImageElement.alt = imageAltText || "";

          // Update dimensions
          if (imageWidth) {
            selectedImageElement.style.width = `${imageWidth}px`;
          } else {
            selectedImageElement.style.removeProperty("width");
          }

          if (imageHeight) {
            selectedImageElement.style.height = `${imageHeight}px`;
          } else {
            selectedImageElement.style.removeProperty("height");
          }

          // Update alignment
          selectedImageElement.style.removeProperty("float");
          selectedImageElement.style.removeProperty("display");
          selectedImageElement.style.removeProperty("margin");

          if (imageAlignment !== "none") {
            switch (imageAlignment) {
              case "left":
                selectedImageElement.style.float = "left";
                selectedImageElement.style.margin = "0 10px 10px 0";
                break;
              case "right":
                selectedImageElement.style.float = "right";
                selectedImageElement.style.margin = "0 0 10px 10px";
                break;
              case "center":
                selectedImageElement.style.display = "block";
                selectedImageElement.style.margin = "10px auto";
                break;
            }
          }

          handleContentChange();
        } else {
          // Insert new image
          // Restore the saved cursor position if available
          if (savedRange) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(savedRange);
          }

          // Create image element with proper attributes
          let imageHTML = `<img src="${finalImageUrl}"`;

          if (imageAltText) {
            imageHTML += ` alt="${imageAltText}"`;
          }

          let styles = [];

          if (imageWidth) {
            styles.push(`width: ${imageWidth}px`);
          }

          if (imageHeight) {
            styles.push(`height: ${imageHeight}px`);
          }

          if (imageAlignment !== "none") {
            switch (imageAlignment) {
              case "left":
                styles.push("float: left", "margin: 0 10px 10px 0");
                break;
              case "right":
                styles.push("float: right", "margin: 0 0 10px 10px");
                break;
              case "center":
                styles.push("display: block", "margin: 10px auto");
                break;
            }
          }

          if (styles.length > 0) {
            imageHTML += ` style="${styles.join("; ")}"`;
          }

          imageHTML += " />";

          executeCommand("insertHTML", imageHTML);

          // Clear the selection and handle content change
          const selection = window.getSelection();
          selection.removeAllRanges();
          handleContentChange();
        }
      } catch (error) {
        console.error("Error applying image:", error);
        // Fallback: insert at the end of the content
        const imageHTML = `<img src="${finalImageUrl}" alt="${
          imageAltText || ""
        }" />`;
        editorRef.current.insertAdjacentHTML("beforeend", imageHTML);
        handleContentChange();
      }

      // Reset form and clear saved range
      setSavedRange(null);
      resetImageForm();
    }
  };

  const resetImageForm = () => {
    setIsImageModalOpen(false);
    setImageUrl("");
    setImageAltText("");
    setImageWidth("");
    setImageHeight("");
    setImageAlignment("none");
    setImageInputType("url");
    setImageFile(null);
    setIsEditingExistingImage(false);
    setSelectedImageElement(null);
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = "";
    }
  };

  const handleEditorClick = (event) => {
    const target = event.target;

    // Check if clicked element is an image
    if (target.tagName === "IMG") {
      event.preventDefault();

      // Set editing mode and store the selected image
      setIsEditingExistingImage(true);
      setSelectedImageElement(target);

      // Populate the form with current image properties
      setImageUrl(target.src || "");
      setImageAltText(target.alt || "");
      setImageWidth(target.style.width || target.width || "");
      setImageHeight(target.style.height || target.height || "");

      // Determine alignment from style
      const float = target.style.float;
      const textAlign = target.style.textAlign;
      if (float === "left") {
        setImageAlignment("left");
      } else if (float === "right") {
        setImageAlignment("right");
      } else if (textAlign === "center") {
        setImageAlignment("center");
      } else {
        setImageAlignment("none");
      }

      // Set input type to URL since we're editing an existing image
      setImageInputType("url");
      setImageFile(null);

      // Open the image modal
      setIsImageModalOpen(true);
    }
  };

  const insertTable = () => {
    setIsTableModalOpen(true);
  };

  const createTable = () => {
    let tableHTML =
      '<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
    for (let i = 0; i < tableRows; i++) {
      tableHTML += "<tr>";
      for (let j = 0; j < tableCols; j++) {
        tableHTML +=
          '<td style="padding: 8px; border: 1px solid #ccc;">Cell</td>';
      }
      tableHTML += "</tr>";
    }
    tableHTML += "</table>";
    executeCommand("insertHTML", tableHTML);
    setIsTableModalOpen(false);
  };

  const highlightText = () => {
    executeCommand("backColor", currentHighlight);
  };

  const removeHighlight = () => {
    executeCommand("backColor", "transparent");
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const findAndReplace = () => {
    setIsFindReplaceOpen(true);
  };

  const performFind = () => {
    if (!findText) return;
    const selection = window.getSelection();
    selection.removeAllRanges();

    if (window.find) {
      window.find(findText);
    }
  };

  const performReplace = () => {
    if (!findText || !replaceText) return;
    const content = editorRef.current.innerHTML;
    const newContent = content.replace(new RegExp(findText, "gi"), replaceText);
    editorRef.current.innerHTML = newContent;
    handleContentChange();
  };

  const performReplaceAll = () => {
    if (!findText || !replaceText) return;
    const content = editorRef.current.innerHTML;
    const newContent = content.replace(new RegExp(findText, "gi"), replaceText);
    editorRef.current.innerHTML = newContent;
    handleContentChange();
  };

  const exportDocument = (format) => {
    const content = editorRef.current.innerHTML;
    let dataStr, filename;

    switch (format) {
      case "html":
        dataStr = "data:text/html;charset=utf-8," + encodeURIComponent(content);
        filename = "document.html";
        break;
      case "txt":
        const plainText = content.replace(/<[^>]*>/g, "");
        dataStr =
          "data:text/plain;charset=utf-8," + encodeURIComponent(plainText);
        filename = "document.txt";
        break;
      default:
        return;
    }

    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", filename);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const importDocument = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      if (file.type === "text/html" || file.name.endsWith(".html")) {
        editorRef.current.innerHTML = content;
      } else {
        editorRef.current.innerHTML = content.replace(/\n/g, "<br>");
      }
      handleContentChange();
    };
    reader.readAsText(file);
  };

  const copyContent = async () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      try {
        const selectedText = selection.toString();
        const range = selection.getRangeAt(0);

        // Create a temporary div to get HTML content
        const tempDiv = document.createElement("div");
        tempDiv.appendChild(range.cloneContents());
        const selectedHTML = tempDiv.innerHTML;

        if (navigator.clipboard && navigator.clipboard.write) {
          // Modern Clipboard API with HTML support
          const clipboardItem = new ClipboardItem({
            "text/html": new Blob([selectedHTML], { type: "text/html" }),
            "text/plain": new Blob([selectedText], { type: "text/plain" }),
          });
          await navigator.clipboard.write([clipboardItem]);
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          // Fallback to text-only clipboard
          await navigator.clipboard.writeText(selectedText);
        } else {
          // Final fallback: show user message
          console.warn(
            "Clipboard API not supported. Please use Ctrl+C to copy."
          );
          alert(
            "Copy not supported. Please use Ctrl+C to copy the selected text."
          );
        }
      } catch (error) {
        console.warn("Copy failed:", error);
        // Show user-friendly message instead of using deprecated API
        alert("Copy failed. Please use Ctrl+C to copy the selected text.");
      }
    }
  };

  const cutContent = async () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      try {
        const selectedText = selection.toString();
        const range = selection.getRangeAt(0);

        // Create a temporary div to get HTML content
        const tempDiv = document.createElement("div");
        tempDiv.appendChild(range.cloneContents());
        const selectedHTML = tempDiv.innerHTML;

        if (navigator.clipboard && navigator.clipboard.write) {
          // Modern Clipboard API with HTML support
          const clipboardItem = new ClipboardItem({
            "text/html": new Blob([selectedHTML], { type: "text/html" }),
            "text/plain": new Blob([selectedText], { type: "text/plain" }),
          });
          await navigator.clipboard.write([clipboardItem]);

          // Delete the selected content
          range.deleteContents();
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          // Fallback to text-only clipboard
          await navigator.clipboard.writeText(selectedText);
          range.deleteContents();
        } else {
          // Final fallback: manual cut operation
          console.warn("Clipboard API not supported. Performing manual cut.");
          range.deleteContents();
          alert("Content cut. Please use Ctrl+V to paste elsewhere.");
        }

        handleContentChange();
      } catch (error) {
        console.warn("Cut failed:", error);
        // Show user-friendly message
        alert("Cut failed. Please use Ctrl+X to cut the selected text.");
        handleContentChange();
      }
    }
  };

  const pasteContent = async () => {
    try {
      if (!navigator.clipboard) {
        // Fallback for browsers without clipboard API
        console.warn("Clipboard API not available. Use Ctrl+V to paste.");
        return;
      }

      const text = await navigator.clipboard.readText();
      if (text && editorRef.current) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();

          // Create a temporary div to parse HTML content
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = text;

          // If the text contains HTML, insert as HTML, otherwise as plain text
          if (tempDiv.children.length > 0) {
            // Insert HTML content
            const fragment = document.createDocumentFragment();
            while (tempDiv.firstChild) {
              fragment.appendChild(tempDiv.firstChild);
            }
            range.insertNode(fragment);
          } else {
            // Insert plain text
            range.insertNode(document.createTextNode(text));
          }

          // Move cursor to end of inserted content
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          // No selection, append to end
          editorRef.current.appendChild(document.createTextNode(text));
        }

        handleContentChange();
      }
    } catch (error) {
      console.error("Failed to paste content:", error);
      // Show user-friendly message
      alert("Paste failed. Please use Ctrl+V to paste content.");
    }
  };

  const clearFormatting = () => {
    executeCommand("removeFormat");
  };

  const printDocument = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Document</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; margin: 2rem; }
            table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
            th, td { border: 1px solid #ddd; padding: 0.75rem; text-align: left; }
            th { background-color: #f2f2f2; }
            blockquote { border-left: 4px solid #ccc; padding-left: 1rem; margin: 1rem 0; font-style: italic; }
            pre { background-color: #f4f4f4; padding: 1rem; border-radius: 4px; overflow-x: auto; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>${editorRef.current.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Advanced emoji insertion
  const insertEmoji = (emoji) => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(emoji));
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      setIsEmojiPickerOpen(false);
      handleContentChange();
    }
  };

  // Enhanced table management
  const insertTableRow = (position = "after") => {
    if (selectedTableCell) {
      const table = selectedTableCell.closest("table");
      const currentRow = selectedTableCell.closest("tr");
      const newRow = currentRow.cloneNode(true);

      // Clear content of new row cells
      newRow.querySelectorAll("td, th").forEach((cell) => {
        cell.textContent = "";
      });

      if (position === "after") {
        currentRow.parentNode.insertBefore(newRow, currentRow.nextSibling);
      } else {
        currentRow.parentNode.insertBefore(newRow, currentRow);
      }

      handleContentChange();
    }
  };

  const insertTableColumn = (position = "after") => {
    if (selectedTableCell) {
      const table = selectedTableCell.closest("table");
      const cellIndex = Array.from(
        selectedTableCell.parentNode.children
      ).indexOf(selectedTableCell);

      table.querySelectorAll("tr").forEach((row) => {
        const newCell = document.createElement(
          row.querySelector("th") ? "th" : "td"
        );
        newCell.textContent = "";

        if (position === "after") {
          row.insertBefore(newCell, row.children[cellIndex + 1]);
        } else {
          row.insertBefore(newCell, row.children[cellIndex]);
        }
      });

      handleContentChange();
    }
  };

  const deleteTableRow = () => {
    if (selectedTableCell) {
      const row = selectedTableCell.closest("tr");
      const table = row.closest("table");

      if (table.querySelectorAll("tr").length > 1) {
        row.remove();
        handleContentChange();
      }
    }
  };

  const deleteTableColumn = () => {
    if (selectedTableCell) {
      const table = selectedTableCell.closest("table");
      const cellIndex = Array.from(
        selectedTableCell.parentNode.children
      ).indexOf(selectedTableCell);

      if (table.querySelector("tr").children.length > 1) {
        table.querySelectorAll("tr").forEach((row) => {
          if (row.children[cellIndex]) {
            row.children[cellIndex].remove();
          }
        });
        handleContentChange();
      }
    }
  };

  // Version history management
  const restoreVersion = (version) => {
    if (onChange) {
      onChange(version.content);
    }
    setIsVersionHistoryOpen(false);
  };

  // Reading mode toggle
  const toggleReadingMode = () => {
    setIsReadingMode(!isReadingMode);
    if (editorRef.current) {
      editorRef.current.contentEditable = isReadingMode;
    }
  };

  // Theme management
  const applyTheme = (theme) => {
    setEditorTheme(theme);
    if (editorRef.current) {
      const editor = editorRef.current;
      if (theme === "dark") {
        editor.style.backgroundColor = "#1f2937";
        editor.style.color = "#f9fafb";
      } else {
        editor.style.backgroundColor = "#ffffff";
        editor.style.color = "#111827";
      }
    }
  };

  // Enhanced spell check
  const toggleSpellCheck = () => {
    setIsSpellCheckEnabled(!isSpellCheckEnabled);
    if (editorRef.current) {
      editorRef.current.spellcheck = !isSpellCheckEnabled;
    }
  };

  const formatBlock = (tag) => {
    executeCommand("formatBlock", tag);
  };

  const changeFontSize = (size) => {
    executeCommand("fontSize", size);
  };

  const changeFontFamily = (font) => {
    executeCommand("fontName", font);
  };

  const changeTextColor = (color) => {
    setCurrentTextColor(color);
    executeCommand("foreColor", color);
  };

  const changeBackgroundColor = (color) => {
    setCurrentBackgroundColor(color);
    executeCommand("backColor", color);
  };

  const changeHighlightColor = (color) => {
    setCurrentHighlight(color);
    executeCommand("hiliteColor", color);
  };

  // Toolbar button sets with proper naming convention
  const toolbarButtonSets = {
    // Set 1: Font Formatting & Typography
    fontFormatting: [
      {
        icon: "format_bold",
        command: "bold",
        title: "Bold (Ctrl+B)",
      },
      {
        icon: "format_italic",
        command: "italic",
        title: "Italic (Ctrl+I)",
      },
      {
        icon: "format_underlined",
        command: "underline",
        title: "Underline (Ctrl+U)",
      },
      {
        icon: "strikethrough_s",
        command: "strikeThrough",
        title: "Strikethrough",
      },
      {
        icon: "subscript",
        command: "subscript",
        title: "Subscript",
      },
      {
        icon: "superscript",
        command: "superscript",
        title: "Superscript",
      },
    ],

    // Set 2: Colors & Highlighting
    colorsHighlighting: [
      // {
      //   icon: FaHighlighter,
      //   action: highlightText,
      //   title: "Highlight Text",
      // },
      {
        icon: "ink_eraser",
        action: removeHighlight,
        title: "Remove Highlight",
      },
    ],

    // Set 3: Text Alignment
    textAlignment: [
      {
        icon: "format_align_left",
        command: "justifyLeft",
        title: "Align Left",
      },
      {
        icon: "format_align_center",
        command: "justifyCenter",
        title: "Align Center",
      },
      {
        icon: "format_align_right",
        command: "justifyRight",
        title: "Align Right",
      },
      {
        icon: "format_align_justify",
        command: "justifyFull",
        title: "Justify",
      },
    ],

    // Set 4: Lists & Indentation
    listsIndentation: [
      {
        icon: "format_list_bulleted",
        command: "insertUnorderedList",
        title: "Bullet List",
      },
      {
        icon: "format_list_numbered",
        command: "insertOrderedList",
        title: "Numbered List",
      },
      {
        icon: "format_indent_increase",
        command: "indent",
        title: "Increase Indent",
      },
      {
        icon: "format_indent_decrease",
        command: "outdent",
        title: "Decrease Indent",
      },
    ],

    historyOperations: [
      {
        icon: "undo",
        command: "undo",
        title: "Undo (Ctrl+Z)",
      },
      {
        icon: "redo",
        command: "redo",
        title: "Redo (Ctrl+Y)",
      },
    ],
    // Set 5: Edit Operations

    editOperations: [
      {
        icon: "content_copy",
        action: copyContent,
        title: "Copy (Ctrl+C)",
      },
      {
        icon: "content_cut",
        action: cutContent,
        title: "Cut (Ctrl+X)",
      },
      {
        icon: "content_paste",
        action: pasteContent,
        title: "Paste (Ctrl+V)",
      },
    ],

    // Set 6: Insert Elements
    insertElements: [
      {
        icon: "link",
        action: insertLink,
        title: hasTextSelection ? "Insert Link" : "Select text to insert link",
        disabled: !hasTextSelection,
      },
      {
        icon: "photo",
        action: insertImage,
        title: "Insert Image",
      },
      {
        icon: "border_all",
        action: insertTable,
        title: "Insert Table",
      },
    ],

    // Set 7: Special Formatting
    // specialFormatting: [
    //   {
    //     icon: FaQuoteLeft,
    //     command: "formatBlock",
    //     value: "blockquote",
    //     title: "Quote",
    //   },
    //   {
    //     icon: FaCode,
    //     command: "formatBlock",
    //     value: "pre",
    //     title: "Code Block",
    //   },
    // ],

    // Set 8: File Operations
    fileOperations: [
      {
        icon: "border_all",
        action: importDocument,
        title: "Import Document",
      },
      {
        icon: "file_save",
        action: () => exportDocument("html"),
        title: "Export as HTML",
      },
      {
        icon: "adf_scanner",
        action: printDocument,
        title: "Print Document",
      },
    ],

    // Set 9: Advanced Tools
    advancedTools: [
      {
        icon: "document_search",
        action: findAndReplace,
        title: "Find & Replace",
      },
      {
        icon: "format_clear",
        action: clearFormatting,
        title: "Clear Formatting",
      },
      // {
      //   icon: isFullScreen ? fullscreen_exit : fullscreen,
      //   action: toggleFullScreen,
      //   title: isFullScreen ? "Exit Full Screen" : "Full Screen",
      // },
    ],
    extraTools: [
      {
        icon: "mood",
        action: () => setIsEmojiPickerOpen(true),
        title: "Insert Emoji",
      },
      {
        icon: "timelapse",
        action: () => setIsVersionHistoryOpen(true),
        title: "Version History",
      },
      {
        icon: "auto_stories",
        action: toggleReadingMode,
        title: "Reading Mode",
      },
      {
        icon: "settings",
        action: () => setIsSettingsOpen(true),
        title: "Editor Settings",
      },
    ],
  };

  // Convert sets object to array for rendering (maintain order)
  const toolbarButtonGroups = Object.values(toolbarButtonSets);

  // Easy access to specific button sets for customization
  // Example: const fontButtons = toolbarButtonSets['font-formatting'];
  // Example: const colorButtons = toolbarButtonSets['colors-highlighting'];
  // Example: const alignmentButtons = toolbarButtonSets['text-alignment'];

  // Flatten the groups for backward compatibility if needed
  const toolbarButtons = toolbarButtonGroups.flat();

  const ToolButton = ({ title, icon, onClick, disabled }) => {
    return (
      <button
        title={title}
        onClick={onClick}
        className={`w-6 h-6 p-1 bg-gray-100 flex items-center justify-center ${
          disabled && "text-gray-300 pointer-events-none"
        }`}
        disabled={disabled}
      >
        <span className="material-symbols-rounded !text-[16px]">{icon}</span>
      </button>
    );
  };

  const ToolSelect = ({ options, onChange }) => {
    return (
      <div>
        <select
          onChange={onChange}
          className="p-1 leading-0 bg-gray-50 text-xs w-fit"
        >
          {options.map((item, key) => {
            return (
              <option key={key} value={item.value}>
                {item.title}
              </option>
            );
          })}
        </select>
      </div>
    );
  };

  const renderButtonSet = (buttonSet) => {
    return buttonSet.map((button, index) => {
      const Icon = button.icon;
      const isDisabled = button.disabled;
      return (
        <ToolButton
          key={index}
          title={button.title}
          disabled={isDisabled}
          icon={Icon}
          onClick={() => {
            if (isDisabled) return;
            if (button.action) {
              button.action();
            } else {
              executeCommand(button.command, button.value);
            }
          }}
        />
      );
    });
  };

  return (
    <div
      className={`${
        isFullScreen
          ? "fixed inset-0 z-50 bg-white"
          : "border border-gray-300 rounded-lg  flex flex-col "
      } ${className}`}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 border-b border-gray-200">
        {/* Toolbar Button Groups */}
        {toolbarButtonSets.historyOperations.map((button, index) => {
          const Icon = button.icon;
          const isDisabled = button.disabled;
          return (
            <ToolButton
              key={index}
              title={button.title}
              disabled={isDisabled}
              icon={Icon}
              onClick={() => {
                if (isDisabled) return;
                if (button.action) {
                  button.action();
                } else {
                  executeCommand(button.command, button.value);
                }
              }}
            />
          );
        })}

        {/* Format Dropdown */}
        <ToolSelect
          onChange={(e) => formatBlock(e.target.value)}
          options={[
            { title: "Select Format", value: "" },
            { title: "Paragraph", value: "p" },
            { title: "Heading 1", value: "h1" },
            { title: "Heading 2", value: "h2" },
            { title: "Heading 3", value: "h3" },
            { title: "Heading 4", value: "h4" },
            { title: "Heading 5", value: "h5" },
            { title: "Heading 6", value: "h6" },
          ]}
        />

        {/* Font Family Dropdown */}
        <ToolSelect
          onChange={(e) => changeFontFamily(e.target.value)}
          options={[
            { title: "Select Font", value: "" },
            { title: "Arial", value: "Arial" },
            { title: "Helvetica", value: "Helvetica" },
            { title: "Times New Roman", value: "Times New Roman" },
            { title: "Courier New", value: "Courier New" },
            { title: "Verdana", value: "Verdana" },
            { title: "Georgia", value: "Georgia" },
          ]}
        />

        {/* Font Size Dropdown */}
        <ToolSelect
          onChange={(e) => changeFontSize(e.target.value)}
          options={[
            { title: "Font Size", value: "" },
            { title: "8pt", value: "1" },
            { title: "10pt", value: "2" },
            { title: "12pt", value: "3" },
            { title: "14pt", value: "4" },
            { title: "18pt", value: "5" },
            { title: "24pt", value: "6" },
            { title: "36pt", value: "7" },
          ]}
        />

        {renderButtonSet(toolbarButtonSets.fontFormatting)}
        {renderButtonSet(toolbarButtonSets.textAlignment)}
        {renderButtonSet(toolbarButtonSets.listsIndentation)}
        {renderButtonSet(toolbarButtonSets.editOperations)}
        {renderButtonSet(toolbarButtonSets.fileOperations)}

        {/* Color Pickers */}
        <ColorPicker
          type="text"
          currentColor={currentTextColor}
          onColorChange={changeTextColor}
          label="Text Color"
          icon={"format_color_text"}
        />

        <ColorPicker
          type="background"
          currentColor={currentBackgroundColor}
          onColorChange={changeBackgroundColor}
          label="Background Color"
          icon={"format_color_fill"}
        />

        <ColorPicker
          type="highlight"
          currentColor={currentHighlight}
          onColorChange={(color) => {
            setCurrentHighlight(color);
            changeHighlightColor(color);
          }}
          label="Highlight Color"
          icon={"format_ink_highlighter"}
        />
        {renderButtonSet(toolbarButtonSets.insertElements)}
        {renderButtonSet(toolbarButtonSets.extraTools)}

        {/* Additional Tools */}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".html,.txt"
        onChange={handleFileImport}
        className="hidden"
      />

      {/* Editor Content */}
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        className={`p-4 outline-none overflow-auto  flex-grow ${
          readOnly ? "bg-gray-50 cursor-not-allowed" : ""
        }`}
        style={{ minHeight: isFullScreen ? "calc(100vh - 120px)" : height }}
        onInput={handleContentChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onClick={handleEditorClick}
        onFocus={onFocus}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      <div className="bg-gray-100 py-1 px-3 text-sm flex justify-between">
        <div>
          {/* Word Count Display */}
          {showWordCount && (
            <div className="ml-auto flex items-center gap-4 text-xs text-gray-600">
              <span>Words: {wordCount.words}</span>
              <span>Characters: {wordCount.characters}</span>
              <span>No Spaces: {wordCount.charactersNoSpaces}</span>
              {maxLength && (
                <span
                  className={
                    wordCount.charactersNoSpaces > maxLength
                      ? "text-red-500"
                      : ""
                  }
                >
                  Limit: {wordCount.charactersNoSpaces}/{maxLength}
                </span>
              )}
            </div>
          )}
        </div>
        <div>
          {/* Auto-save indicator */}
          <div className="flex items-center gap-2 text-sm text-gray-500 ml-4">
            {isAutoSaveEnabled && (
              <div className="flex items-center gap-1">
                <FaSave className="w-3 h-3 text-green-500" />
                <span>Auto-save</span>
                {lastSaved && (
                  <span className="text-xs">
                    {lastSaved.toLocaleTimeString()}
                  </span>
                )}
              </div>
            )}
            {collaborators.length > 0 && (
              <div className="flex items-center gap-1">
                <FaUsers className="w-3 h-3 text-blue-500" />
                <span>{collaborators.length}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Link Modal */}
      <LinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        linkUrl={linkUrl}
        setLinkUrl={setLinkUrl}
        selectedText={selectedText}
        isEditingExistingLink={isEditingExistingLink}
        onApplyLink={applyLink}
        onCancel={() => {
          setLinkUrl("");
          setSelectedText("");
          setSavedRange(null);
          setIsEditingExistingLink(false);
        }}
      />

      <ImageModal
        isOpen={isImageModalOpen}
        onClose={resetImageForm}
        imageInputType={imageInputType}
        setImageInputType={setImageInputType}
        imageUrl={imageUrl}
        setImageUrl={setImageUrl}
        imageFile={imageFile}
        onImageFileChange={handleImageFileChange}
        imageAltText={imageAltText}
        setImageAltText={setImageAltText}
        imageWidth={imageWidth}
        setImageWidth={setImageWidth}
        imageHeight={imageHeight}
        setImageHeight={setImageHeight}
        imageAlignment={imageAlignment}
        setImageAlignment={setImageAlignment}
        isEditingExistingImage={isEditingExistingImage}
        onApplyImage={applyImage}
        onResetImageForm={resetImageForm}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      />

      <TableModal
        isOpen={isTableModalOpen}
        onClose={() => setIsTableModalOpen(false)}
        tableRows={tableRows}
        setTableRows={setTableRows}
        tableCols={tableCols}
        setTableCols={setTableCols}
        onCreateTable={createTable}
      />

      <FindReplaceModal
        isOpen={isFindReplaceOpen}
        onClose={() => {
          setIsFindReplaceOpen(false);
          setFindText("");
          setReplaceText("");
        }}
        findText={findText}
        setFindText={setFindText}
        replaceText={replaceText}
        setReplaceText={setReplaceText}
        onPerformFind={performFind}
        onPerformReplace={performReplace}
        onPerformReplaceAll={performReplaceAll}
      />

      <EmojiPickerModal
        isOpen={isEmojiPickerOpen}
        onClose={() => setIsEmojiPickerOpen(false)}
        onInsertEmoji={insertEmoji}
      />

      <VersionHistoryModal
        isOpen={isVersionHistoryOpen}
        onClose={() => setIsVersionHistoryOpen(false)}
        versionHistory={versionHistory}
        onRestoreVersion={restoreVersion}
      />

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        isAutoSaveEnabled={isAutoSaveEnabled}
        setIsAutoSaveEnabled={setIsAutoSaveEnabled}
        isSpellCheckEnabled={isSpellCheckEnabled}
        onToggleSpellCheck={toggleSpellCheck}
        editorTheme={editorTheme}
        onApplyTheme={applyTheme}
        fontSize={fontSize}
        setFontSize={setFontSize}
        lineHeight={lineHeight}
        setLineHeight={setLineHeight}
      />

      <TableContextMenu
        show={tableContextMenu.show}
        x={tableContextMenu.x}
        y={tableContextMenu.y}
        onInsertRowBefore={() => {
          insertTableRow("before");
          setTableContextMenu({ show: false, x: 0, y: 0, type: "" });
        }}
        onInsertRowAfter={() => {
          insertTableRow("after");
          setTableContextMenu({ show: false, x: 0, y: 0, type: "" });
        }}
        onInsertColumnBefore={() => {
          insertTableColumn("before");
          setTableContextMenu({ show: false, x: 0, y: 0, type: "" });
        }}
        onInsertColumnAfter={() => {
          insertTableColumn("after");
          setTableContextMenu({ show: false, x: 0, y: 0, type: "" });
        }}
        onDeleteRow={() => {
          deleteTableRow();
          setTableContextMenu({ show: false, x: 0, y: 0, type: "" });
        }}
        onDeleteColumn={() => {
          deleteTableColumn();
          setTableContextMenu({ show: false, x: 0, y: 0, type: "" });
        }}
        onClose={() =>
          setTableContextMenu({ show: false, x: 0, y: 0, type: "" })
        }
      />

      {/* Custom Styles */}
      <CkTextEditorStyles
        lineHeight={lineHeight}
        fontSize={fontSize}
        editorTheme={editorTheme}
      />
    </div>
  );
};

export default CkTextEditor;
